import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {BaseComponent} from "@wms/components";
import {AccordionModule} from "ngx-bootstrap/accordion";
import {FormsModule, NgForm} from "@angular/forms";
import {CommonModule} from "@angular/common";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {_removeProperty, assignNewPropIndexByType, Config, Method, Parameter, PrefabConfigurationService, removeUndefinedKeys} from "@wms/settings/prefab-config";
import {getClonedObject, isDefined, runtimeEventManager} from "@wm/core";
import {capitalize, extend, filter, includes, isArray, isEmpty, isEqual, property, pull} from "lodash-es";
import {forEach} from "lodash";
import {Subscription} from "rxjs";
import { BasePrefabConfigTab } from '../base-tab-component/base-prefabconfig-tab-component.component';

@Component({
  selector: 'wms-methods',
  templateUrl: './methods.component.html',
  styleUrls: ['./methods.component.less'],
  imports: [
    AccordionModule,
    CommonModule,
    FormsModule,
    TooltipModule
  ],
  standalone: true
})
export class MethodsComponent extends BasePrefabConfigTab implements OnInit {
  showMethods: boolean;
  noMethodsAvailable: boolean;
  returnTypes: string[];
  paramTypes: string[];
  methods: Method[];
  selectedMethod: Method;
  selectedMethodIndex: number;
  newMethodIndex: number;
  nameErrMsg: string;
  inValidProperties: any;
  formValueChangesSubscription: Subscription;
  @ViewChild('methodsForm', {static: true}) override methodsForm: NgForm;

  constructor(private elementRef: ElementRef) {
    super();
    this.showMethods = true;
    this.newMethodIndex = 1;
    this.returnTypes = ['void', 'any', 'string', 'number', 'boolean', 'date'];
    this.paramTypes = ['any', 'string', 'number', 'boolean', 'date'];
    this.inValidProperties = { 'UIProp' : [] };
    this.registerDestroyListener.add(runtimeEventManager.on('prepare-methods-save').subscribe(() => this.prepareForSave()));
  }

  ngOnInit() {
    let setInitialValue: boolean = true;
    if (this.initialMethods) {        
      setInitialValue = false;
    }
    this.prepareModelForDisplay(this.prefabConfigService.getConfig());

    this.formValueChangesSubscription = this.methodsForm.form.valueChanges.subscribe(() => {
      if (!this.methodsForm.form.dirty && setInitialValue) {
        this.initialMethods = this.methodsForm.form.value;
      } 
      if(this.methodsForm.form.dirty && !isEqual(this.initialMethods,this.methodsForm.form.value))
        this.setDirty()
    });

    this.resetFormSubscription = this.prefabConfigService.resetForm.subscribe((resetInitialVal) => {
      if (!resetInitialVal) {
        this.prepareModelForDisplay(this.prefabConfigService.getConfig());
      }
    });
  }

  setDirty() {
    this.prefabConfigService.isDirty['methods'] = true;
  }

  prepareForSave() {
    let methods: any = {}, methodsObj = getClonedObject(this.methods);
    forEach(methodsObj, (methodObj: Method, methodName: string) => {
      if (!methodObj.name)
        return;

      let method: Method = {
        description : isDefined(methodObj['description']) ? methodObj['description'] : '',
        parameters  : [],
        returnType  : isDefined(methodObj.returnType) ? methodObj.returnType : 'void',
        example     : ''
      };
      methodObj.parameters.forEach((param: Parameter) => {
        if(!param.name)
          return;

        let paramObj: Parameter = {
          name        : param.name,
          type        : isDefined(param.type) ? param.type : '',
          description : isDefined(param.description) ? param.description : ''
        };
        method.parameters.push(paramObj);
      });
      methods[methodObj.name] = method;
    });
    this.prefabConfig.methods = methods;
  }

  removeMethod(method: Method) {
    _removeProperty(this.methods, method);
    this.prefabConfig.methods = this.methods;
    this.setDirty();
    this.selectFirstMethod();
  }

  focusField(id: string) {
    let field: HTMLElement = this.elementRef.nativeElement.querySelector(id);
    if(field) {
      field.focus();
    }
  }

  selectMethod(method: any, index: number) {
    this.selectedMethod       = method;
    this.selectedMethodIndex  = index;
    this.setMethodValidity(method); //after adding the properties check for the invalid state of the property
    setTimeout( () => {
      this.methodsForm.controls['methodName'].markAsPristine();
      this.methodsForm.controls['methodName'].markAsUntouched();
      this.focusField('input[name=methodName]');
    });
  }

  selectFirstMethod() {
    if (!isEmpty(this.methods)) {
      this.selectedMethod = this.methods[0];
      this.selectedMethodIndex = 0;
      this.focusField('input[name=methodName]');
    } else {
      this.noMethodsAvailable = true;
    }
  }

  prepareModelForDisplay(config: Config) {
    this.prefabConfig = config;
    let methods: any = config.methods || {};
    methods = Object.keys(methods).map( (methodName) => {
      let method = methods[methodName];
      if(!isDefined(method.parameters) || !isArray(method.parameters)) {
        method.parameters = [];
      }
      return extend({}, {name: method.name || method.tempName || methodName}, method);
    });
    this.methods = removeUndefinedKeys(methods);
    this.selectFirstMethod();
  }

  addMethod(event: any) {
    event.stopPropagation();
    event.preventDefault();
    assignNewPropIndexByType('method', this.newMethodIndex, 'method', this.methods);
    this.showMethods = true;
    this.noMethodsAvailable = false;
    this.methods.push({
      tempName    : 'method' + this.newMethodIndex,
      type        : 'method',
      parameters  : [],
      returnType  : 'void'
    });
    this.prefabConfig.methods = this.methods;
    this.setDirty();
    this.selectMethod(this.methods[this.methods.length - 1], this.methods.length - 1);
    this.newMethodIndex++;
  };

  addNewMethodParam(method: Method) {
    method.parameters.push({ type: 'any' });
    setTimeout( () => {
      this.focusField('table.method-params tr:last-child td:first-child input');
    });
    this.setDirty();
  }

  removeMethodParam(param: Parameter, method: Method) {
    _removeProperty(method.parameters, param);
    this.setDirty();
  }

  validateFields(method: Method, type: string) {
    switch (type) {
      case 'name':
        method['nameInvalid'] = method.name && this.checkUIPropertyExists(method) || !method.name ? true : false;
        if(method['nameInvalid']) {
          this.nameErrMsg = method.name && this.checkUIPropertyExists(method) ? this.locale.MESSAGE_PREFAB_ERROR_DUPLICATE_PROPERTY_NAME : this.locale.MESSAGE_PREFAB_ERROR_PATTERN_PROPERTY_NAME;
        } else {
          method['displayName'] = method['displayName'] || capitalize(method.name);
        }
        break;
      case 'description':
        method['descriptionInvalid'] = !method['description'];
        break;
    }
    this.setMethodValidity(method);
  }

  setPropValidity(propertyName?: string, isValid?: boolean) {
    if (propertyName) {
      if (!isValid) {
        this.inValidProperties.UIProp.push(propertyName);
      } else if (includes(this.inValidProperties.UIProp, propertyName)) {
        pull(this.inValidProperties.UIProp, propertyName);
        this.prefabConfigService.hideErrorMsg$.next();
      }
    }
    forEach(this.inValidProperties.UIProp, (methodName: string) => {
      if (filter(this.methods, {name: methodName}).length <= 1) {
        pull(this.inValidProperties.UIProp, methodName);
      }
    });
    this.prefabConfigService.isInvalid['methods'] = !isEmpty(this.inValidProperties.UIProp) || (this.selectedMethod && this.selectedMethod['invalid']) || this.methodsForm.form.invalid;
  }

  setMethodValidity(method: Method) {
    method['invalid'] = method['nameInvalid'] || !method.name || !method['description'];
    this.setPropValidity(method.name, !method['invalid']);
  }

  checkUIPropertyExists(currentMethod: Method) {
    let nameExists = false;
    // @ts-ignore
    forEach(this.methods, (method: Method) => {
      if (!isEqual(method, currentMethod) && method.name === currentMethod.name) {
        nameExists = true;
        return false;
      }
    });
    return nameExists;
  }

  override ngOnDestroy() {
    this.formValueChangesSubscription.unsubscribe();
  }

  protected readonly property = property;
}
